import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpInterceptorFn,
} from '@angular/common/http'
import { inject } from '@angular/core'

import { catchError, switchMap, throwError } from 'rxjs'

import { environment } from '../../../../environments/environment'
import { AuthService } from '../services/auth.service'

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService)
  const httpBackend = inject(HttpBackend)
  const rawClient = new HttpClient(httpBackend)
  const token = authService.getToken()

  if (token) {
    const cloned = req.clone({
      headers: req.headers.set('Authorization', `Bearer ${token}`),
    })
    return next(cloned).pipe(
      catchError((error: HttpErrorResponse) => {
        // Avoid refresh loops on authentication or refresh endpoints
        const isAuthPath = req.url.includes('/auth/authentication')
        const isRefreshPath = req.url.includes('/auth/refresh')
        if (error.status === 401 && !isAuthPath && !isRefreshPath) {
          const csrf = authService.getCsrfToken()
          const headers = new HttpHeaders({ 'X-CSRF-Token': csrf ?? '' })
          // Attempt to refresh the access token using a raw client (no interceptors)
          return rawClient
            .post<any>(
              `${environment.apiBase}auth/refresh`,
              {},
              {
                withCredentials: true,
                headers,
                observe: 'response',
              },
            )
            .pipe(
              switchMap((resp) => {
                // Accept either { data: { access_token }} or { access_token }
                const body = resp.body || {}
                const newToken = body?.data?.access_token || body?.access_token
                if (newToken) {
                  authService.setToken(newToken)
                  const retried = req.clone({
                    headers: req.headers.set('Authorization', `Bearer ${newToken}`),
                  })
                  return next(retried)
                }
                // No token returned, force logout
                return authService
                  .logout('token_expired')
                  .pipe(switchMap(() => throwError(() => error)))
              }),
              catchError((refreshErr) => {
                // Refresh failed, perform logout and propagate original error
                return authService
                  .logout('invalid_token')
                  .pipe(switchMap(() => throwError(() => error)))
              }),
            )
        }
        return throwError(() => error)
      }),
    )
  }

  // No token present, proceed as-is
  return next(req)
}
